/**
 * Scene upload pipeline hook (T038, port of hooks/use_upload.rs).
 *
 * Flow: read file bytes → SHA-256 hex (Web Crypto) → presign → PUT to R2 →
 * confirm. Drives a `UploadState` (the union shared from UploadIndicator:
 * idle/reading/uploading/completed/failed) so the page can render progress.
 *
 * The presign body mirrors `ApiClient::presign_scene` (rm-ui api.rs): a fixed
 * `scene.tar.zst` filename plus a single-entry `FileManifest`. The Rust hook
 * uses the SHA-256 hex for the manifest `blake3_hash` field (64 hex chars, same
 * length as BLAKE3) — we replicate that exactly.
 *
 * // TODO known-limit 10GB: full buffering + one-shot SHA-256; streaming (Blob
 * // body + chunked hash in a Worker) is a separate follow-up
 */
import { useCallback, useState } from 'react'
import { presignScene, confirmScene, uploadToR2 } from '../lib/endpoints/upload'
import { ApiError } from '../lib/errors'
import type { UploadState } from '../components/UploadIndicator'

/** Single-file manifest, mirrors scene_package::FileManifest / FileManifestEntry. */
interface FileManifestEntry {
  path: string
  size_bytes: number
  blake3_hash: string
}
interface FileManifest {
  files: FileManifestEntry[]
  total_size_bytes: number
  file_count: number
}

export interface UseUploadResult {
  state: UploadState
  /** Run the full pipeline for a job. Resolves to the scene_url (may be empty). */
  upload: (jobId: string, file: File) => Promise<string>
  reset: () => void
}

/** Read a File into an ArrayBuffer (FileReader equivalent). */
async function readFileBytes(file: File): Promise<ArrayBuffer> {
  return await file.arrayBuffer()
}

/** Compute SHA-256 of bytes using Web Crypto. Returns a 64-char hex string. */
async function computeSha256(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return String(error)
}

export function useUpload(): UseUploadResult {
  const [state, setState] = useState<UploadState>({ kind: 'idle' })

  const reset = useCallback(() => setState({ kind: 'idle' }), [])

  const upload = useCallback(
    async (jobId: string, file: File): Promise<string> => {
      const fileName = file.name
      setState({ kind: 'reading', fileName })

      // Step 1: Read file bytes.
      let bytes: ArrayBuffer
      try {
        bytes = await readFileBytes(file)
      } catch (error) {
        setState({
          kind: 'failed',
          step: 'reading',
          message: `Blad odczytu pliku: ${errorMessage(error)}`,
          retryable: true,
        })
        throw error
      }
      const size = bytes.byteLength

      // Step 2: Compute SHA-256 hash (64 hex chars, same format as BLAKE3).
      setState({ kind: 'uploading', progress: 0.0, fileName })
      let hash: string
      try {
        hash = await computeSha256(bytes)
      } catch (error) {
        setState({
          kind: 'failed',
          step: 'hashing',
          message: `Blad obliczania hash: ${errorMessage(error)}`,
          retryable: true,
        })
        throw error
      }

      // Step 3: Build manifest (single file for web upload).
      const manifest: FileManifest = {
        files: [{ path: fileName, size_bytes: size, blake3_hash: hash }],
        total_size_bytes: size,
        file_count: 1,
      }

      // Step 4: Call presign API.
      setState({ kind: 'uploading', progress: 0.1, fileName })
      let presignedUrl: string
      try {
        const presign = await presignScene(jobId, {
          filename: 'scene.tar.zst',
          archive_size: size,
          archive_hash: hash,
          manifest,
        })
        presignedUrl = presign.presigned_url
      } catch (error) {
        setState({
          kind: 'failed',
          step: 'presign',
          message: errorMessage(error),
          retryable: true,
        })
        throw error
      }

      // Step 5: Upload to R2 via presigned PUT URL.
      setState({ kind: 'uploading', progress: 0.3, fileName })
      try {
        await uploadToR2(presignedUrl, bytes, 'application/zstd')
      } catch (error) {
        setState({
          kind: 'failed',
          step: 'upload',
          message: errorMessage(error),
          retryable: true,
        })
        throw error
      }

      // Step 6: Confirm upload.
      setState({ kind: 'uploading', progress: 0.9, fileName })
      let sceneUrl: string
      try {
        const confirm = await confirmScene(jobId, hash, size)
        sceneUrl = confirm.data.scene_url ?? ''
      } catch (error) {
        setState({
          kind: 'failed',
          step: 'confirm',
          message: errorMessage(error),
          retryable: true,
        })
        throw error
      }

      setState({ kind: 'uploading', progress: 1.0, fileName })
      setState({ kind: 'completed', sceneKey: sceneUrl })
      return sceneUrl
    },
    [],
  )

  return { state, upload, reset }
}
